import {
    ProtocolMode,
    type Configuration,
    type BrowserCacheLocation,
    PublicClientApplication,
} from '@azure/msal-browser';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
    getMeta,
    getMetaOrFail,
} from '@precon-tools-suite-by-shared/browser-utils';
import { createMsalLoggerOptions } from '@precon-tools-suite-by-shared/pretty-console';

export type MsalOptions = {
    loginRedirectUri?: string;
    logoutRedirectUri?: string;
    msalCacheLocation?: BrowserCacheLocation;
    appName?: string;
    appVersion: string;
    appId?: string;
    appInsights?: ApplicationInsights;
    logMsalEvents?: boolean;
} & GetMsalAuthorityOptions;

function getDefaultRedirectUri() {
    const routePrefix = getMeta('route-prefix');
    if (routePrefix) {
        return `/${routePrefix}`;
    } else {
        return window.location.origin;
    }
}

function createBackendMsalConfiguration(options: MsalOptions): Configuration {
    return {
        auth: {
            clientId: options.appId ?? getMetaOrFail('app-client-id'),
            protocolMode: ProtocolMode.AAD,
            authority: getMsalAuthority(options),
            knownAuthorities: [
                'login.microsoftonline.com',
                'precontools.b2clogin.com',
                'precontoolsstaging.b2clogin.com',
                'precontoolsdevelopment.b2clogin.com',
            ],
            redirectUri: options.loginRedirectUri ?? getDefaultRedirectUri(),
            postLogoutRedirectUri:
                options.logoutRedirectUri ?? getDefaultRedirectUri(),
        },
        cache: {
            cacheLocation: options.msalCacheLocation ?? 'sessionStorage',
            storeAuthStateInCookie: false,
            secureCookies: false,
        },
        telemetry: {
            application: {
                appName: options.appName ?? getMetaOrFail('app-name'),
                appVersion: options.appVersion,
            },
        },
        system: {
            windowHashTimeout: 60_000,
            iframeHashTimeout: 6_000,
            loadFrameTimeout: 0,
            asyncPopups: false,
            loggerOptions: {
                ...createMsalLoggerOptions({
                    debugMode: options.logMsalEvents,
                }),
            },
        },
    };
}

export function createBackendMsalInstance(
    options: MsalOptions
): PublicClientApplication {
    return new PublicClientApplication(createBackendMsalConfiguration(options));
}

export type GetMsalAuthorityOptions = {
    authority?: string;
    loginWithTeam?: boolean;
    loginPolicy?: string;
    b2cTenantDomain?: string;
};

export function getMsalAuthority(options: GetMsalAuthorityOptions) {
    if (options.authority) {
        return options.authority;
    }

    if (options.loginPolicy) {
        return getB2CPolicyAuthority(
            options.loginPolicy,
            options.b2cTenantDomain ?? getMetaOrFail('b2c-tenant-domain')
        );
    }

    // const authority = getMeta('msal-authority');
    // if (authority) return authority;

    return getB2CPolicyAuthority(
        options.loginWithTeam
            ? getMetaOrFail('b2c-login-with-team-policy-name')
            : getMetaOrFail('b2c-login-policy-name'),
        options['b2cTenantDomain'] ?? getMetaOrFail('b2c-tenant-domain')
    );
}

export function getB2CPolicyAuthority(policy: string, tenantDomain?: string) {
    tenantDomain ??=
        getMeta('b2c-tenant-domain') ?? getMetaOrFail('msal-tenant-id');
    const tenantDomainPrefix =
        getMeta('b2c-tenant-domain-prefix') ?? tenantDomain.split('.')[0];
    return `https://${tenantDomainPrefix}.b2clogin.com/${tenantDomain}/${policy}`;
}
